/**
 * The configuration form, drawn from the backend's schema rather than written
 * out here.
 *
 * That is the point of the whole arrangement: adding a setting later is a
 * backend change and nothing else. Nothing in this file names `num_ctx` or
 * `temperature`; it renders whatever fields arrive, in whatever groups, with
 * whatever presets.
 *
 * The one thing it does understand is `applies`, because the three places a
 * setting can take effect are genuinely different and a form that hid the
 * difference would be lying:
 *
 *   model    baked into a new model. Reaches every client, including the
 *            OpenAI SDKs whose /v1 API has nowhere to put a context length.
 *   load     passed when the model enters the GPU. Not part of the model.
 *   request  the client sends it. We can show a default; we cannot impose it.
 *
 * So only the `model` fields are submitted here. The others are shown, marked,
 * and left to the places that own them — a form that silently dropped them
 * would be worse, because someone would set one and believe it.
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  ApiError,
  configureModel,
  fetchSchema,
  type ConfigSchema,
  type SchemaField,
} from "./api";

type Value = string;

export default function ConfigureDialog({
  model,
  node,
  onClose,
}: {
  model: string;
  /** Which machine holds it. The settings are read from — and written to —
   *  that node, because two machines can hold the same name with different
   *  ceilings. Null on a single-machine install. */
  node?: string | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [values, setValues] = useState<Record<string, Value>>({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const schema = useQuery({
    queryKey: ["ollama", "schema", node ?? "", model],
    queryFn: () => fetchSchema(model, node),
  });

  // Fill the form from what the model is actually set to. Opening on defaults
  // would mean a save that only meant to change temperature also quietly reset
  // the context length back to 4096.
  useEffect(() => {
    if (!schema.data || touched) return;
    const current = schema.data.current ?? {};
    const seed: Record<string, Value> = {};
    for (const field of schema.data.fields) {
      const now = current[field.key];
      if (now !== undefined && !Array.isArray(now)) seed[field.key] = String(now);
    }
    setValues(seed);
  }, [schema.data, touched]);

  const set = (key: string, value: Value) => {
    setTouched(true);
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = { model };
      for (const field of bakeable) {
        const raw = (values[field.key] ?? "").trim();
        if (raw === "") continue; // empty means "leave it to the model"
        body[field.key] = Number(raw);
      }
      // The name is not a PARAMETER — it is what the new model is CALLED — so
      // it rides alongside the baked settings rather than among them. Omitted
      // when empty, which is how the backend is asked to generate one.
      const named = (values.alias ?? "").trim();
      if (named) body.alias = named;
      if (node) body.node = node;
      return configureModel(body as never);
    },
    onMutate: () => setProblem(null),
    onError: (error) =>
      setProblem(error instanceof ApiError ? error.detail : String(error)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["jobs"] });
      void queryClient.invalidateQueries({ queryKey: ["ollama"] });
      onClose();
    },
  });

  const data = schema.data;
  const visible = useMemo(
    () => (data ? data.fields.filter((f) => suits(f, data)) : []),
    [data],
  );
  const bakeable = visible.filter((f) => f.applies === "model" && f.type !== "string");
  const elsewhere = visible.filter((f) => f.applies !== "model");

  // Escape closes. A modal that traps someone is a modal they resent.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const alias = visible.find((f) => f.key === "alias");
  const nothingSet = bakeable.every((f) => !(values[f.key] ?? "").trim());

  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dlg" role="dialog" aria-modal="true" aria-label={`Configure ${model}`}>
        <header>
          <div>
            <h2>Configure</h2>
            <p className="hint">{node ? `${model} on ${node}` : model}</p>
          </div>
          <button className="btn sm" type="button" onClick={onClose}>
            Close
          </button>
        </header>

        {schema.isLoading && <p className="hint pad">Reading the model…</p>}
        {schema.error && (
          <p className="problem pad">
            {schema.error instanceof ApiError
              ? schema.error.detail
              : String(schema.error)}
          </p>
        )}

        {data && (
          <>
            <p className="lede">
              Settings cannot be attached to an Ollama model in place. Saving
              builds a <b>new model</b> with them baked in, sharing the same
              weights on disk — so clients get them by using the new name. This
              is the only way to give a context length to an OpenAI-style
              client, whose API has nowhere to put one.
            </p>

            {data.ceiling != null && (
              <p className="hint pad">
                This model tops out at {data.ceiling.toLocaleString()} tokens.
              </p>
            )}

            <div className="dlg-body">
              {groupsOf(data, visible).map(([group, fields]) => {
                const plain = fields.filter((f) => !f.advanced);
                const adv = fields.filter((f) => f.advanced);
                if (!plain.length && !adv.length) return null;
                return (
                  <section key={group.key}>
                    <h3>{group.label}</h3>
                    {plain.map((f) => (
                      <Field
                        key={f.key}
                        field={f}
                        value={values[f.key] ?? ""}
                        onChange={(v) => set(f.key, v)}
                      />
                    ))}
                    {adv.length > 0 && showAdvanced &&
                      adv.map((f) => (
                        <Field
                          key={f.key}
                          field={f}
                          value={values[f.key] ?? ""}
                          onChange={(v) => set(f.key, v)}
                        />
                      ))}
                    {adv.length > 0 && !showAdvanced && (
                      <button
                        className="linkish"
                        type="button"
                        onClick={() => setShowAdvanced(true)}
                      >
                        Show {adv.length} advanced setting{adv.length > 1 ? "s" : ""}
                      </button>
                    )}
                  </section>
                );
              })}
            </div>

            {elsewhere.length > 0 && (
              <div className="elsewhere">
                <h3>Not part of the model</h3>
                <p className="hint">
                  These take effect somewhere else, so they are not baked in and
                  are not saved here.
                </p>
                <ul>
                  {elsewhere.map((f) => (
                    <li key={f.key}>
                      <b>{f.label}</b>
                      <span className={`chip ${f.applies}`}>
                        {f.applies === "load" ? "set when loading" : "sent by the client"}
                      </span>
                      {f.help && <p className="hint">{f.help}</p>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {problem && <p className="problem pad">{problem}</p>}

            <footer>
              <p className="hint">
                {alias && (values.alias ?? "").trim()
                  ? `Will be saved as ${(values.alias ?? "").trim()}`
                  : "A name will be generated from the model and the context length."}
              </p>
              <div className="rowacts">
                <button className="btn" type="button" onClick={onClose}>
                  Cancel
                </button>
                <button
                  className="btn primary"
                  type="button"
                  disabled={save.isPending || nothingSet}
                  onClick={() => save.mutate()}
                  title={nothingSet ? "Set at least one value first" : undefined}
                >
                  {save.isPending ? "Building…" : "Build model"}
                </button>
              </div>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}

function Field({
  field,
  value,
  onChange,
}: {
  field: SchemaField;
  value: string;
  onChange: (value: string) => void;
}) {
  const id = `cfg-${field.key}`;
  return (
    <div className="fld">
      <label htmlFor={id}>
        {field.label}
        {field.unit && <span className="unit">{field.unit}</span>}
      </label>

      {field.type === "enum" ? (
        <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">Default</option>
          {(field.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ) : field.type === "bool" ? (
        <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">Model default</option>
          <option value="true">On</option>
          <option value="false">Off</option>
        </select>
      ) : (
        <input
          id={id}
          type={field.type === "string" ? "text" : "number"}
          value={value}
          min={field.min ?? undefined}
          max={field.max ?? undefined}
          step={field.type === "float" ? "0.01" : undefined}
          placeholder={
            field.default == null ? "model default" : String(field.default)
          }
          onChange={(e) => onChange(e.target.value)}
        />
      )}

      {/* Presets, because typing 32768 by hand is how a typo becomes a model
          that is silently wrong. */}
      {field.presets && field.presets.length > 0 && (
        <div className="presets">
          {field.presets.map((p) => (
            <button
              key={p}
              type="button"
              className={String(p) === value ? "on" : ""}
              onClick={() => onChange(String(p))}
            >
              {p % 1024 === 0 ? `${p / 1024}k` : p.toLocaleString()}
            </button>
          ))}
          {value && (
            <button type="button" className="clear" onClick={() => onChange("")}>
              clear
            </button>
          )}
        </div>
      )}

      {field.help && <p className="hint">{field.help}</p>}
    </div>
  );
}

/** The backend drops fields the model TYPE cannot use. Capability conditions
 *  are left for here, because capabilities are per model while the schema is
 *  per type — so a thinking default only appears on a model that can think. */
function suits(field: SchemaField, schema: ConfigSchema): boolean {
  const needed = field.show_when?.capability;
  if (!needed) return true;
  const has = schema.capabilities ?? [];
  return needed.some((c) => has.includes(c));
}

function groupsOf(
  schema: ConfigSchema,
  fields: SchemaField[],
): [ConfigSchema["groups"][number], SchemaField[]][] {
  return [...schema.groups]
    .sort((a, b) => a.order - b.order)
    .map((g) => [g, fields.filter((f) => f.group === g.key && f.applies === "model")] as const)
    .filter(([, fs]) => fs.length > 0)
    .map(([g, fs]) => [g, [...fs]]);
}
